import React, {useState, useEffect, useContext} from "react"
import {
	Button,
	Dialog,
	Slide,
	DialogTitle,
	DialogContent,
	DialogActions,
	Grid,
	TextField,
	Divider,
} from "@mui/material"
import {Order} from "../../dtos/orderResponse"
import AlertWithStatus from "../feedback/AlertWithStatus"
import OrderService from "../../services/order/OrderService"
import {ShippingAddressChangeRequest} from "../../services/order/AddressDetails"
import {getCaseId} from "../../utils/UrlParser"
import Dropdown from "../Dropdown/Dropdown"
import {ActionReasonsContext} from "../../contexts/ActionReasonsContext"
import {postActionLog, ButtonActionTypes} from "../../services/order/TrackOrderActions"
import {TransitionProps} from "@mui/material/transitions"

const Transition = React.forwardRef(function Transition(
	props: TransitionProps & {children: React.ReactElement<any, any>},
	ref: React.Ref<unknown>
) {
	return <Slide direction="up" ref={ref} {...props} />
})

interface ShippingAddressProps {
	open: boolean
	close: () => void
	order: Order
	reloadParent: () => void
}

export function ShippingAddress(props: ShippingAddressProps) {
	const [reason, setReason] = useState("")
	const [firstName, setFirstName] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.firstName
			? props.order.orderData.shippingAddress.firstName
			: ""
	)
	const [lastName, setLastName] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.lastName
			? props.order.orderData.shippingAddress.lastName
			: ""
	)
	const [company, setCompany] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.company
			? props.order.orderData.shippingAddress.company
			: ""
	)
	const [streetName, setStreetName] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.streetName
			? props.order.orderData.shippingAddress.streetName
			: ""
	)
	const [additionalStreetInfo, setAdditionalStreetInfo] = useState(
		props.order.orderData.shippingAddress &&
			props.order.orderData.shippingAddress.additionalStreetInfo
			? props.order.orderData.shippingAddress.additionalStreetInfo
			: ""
	)
	const [city, setCity] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.city
			? props.order.orderData.shippingAddress.city
			: ""
	)
	const [state, setState] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.state
			? props.order.orderData.shippingAddress.state
			: ""
	)
	const [postalCode, setPostalCode] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.postalCode
			? props.order.orderData.shippingAddress.postalCode
			: ""
	)
	const [phone, setPhone] = useState(
		props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.phone
			? props.order.orderData.shippingAddress.phone
			: ""
	)
	const [delta, setDelta] = useState({})
	const {reasons} = useContext(ActionReasonsContext)

	const [popOpened, setPopOpened] = useState(false)
	const [popType, setPopType] = useState<string | null>(null)
	const [popMessage, setPopMessage] = useState<string | undefined>(undefined)
	function openPop(popType: string, message: string | undefined) {
		setPopType(popType)
		setPopOpened(true)
		setPopMessage(message)
	}
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault()
		const {value} = event.currentTarget
		switch (event.target.id) {
			case "firstName":
				setFirstName(value)
				break
			case "lastName":
				setLastName(value)
				break
			case "company":
				setCompany(value)
				break
			case "streetName":
				setStreetName(value)
				break
			case "additionalStreetInfo":
				setAdditionalStreetInfo(value)
				break
			case "city":
				setCity(value)
				break
			case "state":
				setState(value)
				break
			case "phone":
				setPhone(value)
				break
			case "postalCode":
				setPostalCode(value)
				break
			default:
				break
		}
		if (event.target.id !== "reason") setDelta({...delta, [event.target.id]: value})
	}

	const onSubmit = async () => {
		if (reason === "") {
			openPop("err", "Please select a reason to proceed.")
			return
		}
		let diff = Object.keys(delta).reduce(diffReducer, {})
		if (Object.keys(diff).length !== 0) {
			for (const item in diff) {
				const field = item.replace(/([A-Z])/g, " $1")
				if (item !== "additionalStreetInfo" && item !== "company" && diff[item].trim() === "") {
					openPop("err", field.charAt(0).toUpperCase() + field.slice(1) + " cannot be blank!")
					return
				}
			}
			const changed = await updateShippingAddress({
				shippingAddress: delta,
				reason: reason,
				orderNumber: props.order.orderNumber,
				caseId: getCaseId(),
			})
			if (!changed) {
				openPop("err", "Failed to request changing Shipping Address")
				props.close()
				return
			} else {
				props.reloadParent()
				openPop("scs", "Shipping Address Change Request is sent successfully")
				props.close()
				return
			}
		}
	}

	const diffReducer = (diff: any, key: any) => {
		if ((props.order.orderData.shippingAddress as any)[key] === (delta as any)[key]) return diff
		return {
			...diff,
			[key]: (delta as any)[key],
		}
	}

	useEffect(() => {
		setReason("")
		setDelta({})
		setFirstName(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.firstName
				? props.order.orderData.shippingAddress.firstName
				: ""
		)
		setLastName(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.lastName
				? props.order.orderData.shippingAddress.lastName
				: ""
		)
		setCompany(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.company
				? props.order.orderData.shippingAddress.company
				: ""
		)
		setStreetName(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.streetName
				? props.order.orderData.shippingAddress.streetName
				: ""
		)
		setAdditionalStreetInfo(
			props.order.orderData.shippingAddress &&
				props.order.orderData.shippingAddress.additionalStreetInfo
				? props.order.orderData.shippingAddress.additionalStreetInfo
				: ""
		)
		setCity(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.city
				? props.order.orderData.shippingAddress.city
				: ""
		)
		setState(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.state
				? props.order.orderData.shippingAddress.state
				: ""
		)
		setPostalCode(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.postalCode
				? props.order.orderData.shippingAddress.postalCode
				: ""
		)
		setPhone(
			props.order.orderData.shippingAddress && props.order.orderData.shippingAddress.phone
				? props.order.orderData.shippingAddress.phone
				: ""
		)
	}, [props])

	const onBackClick = () => {
		postActionLog(
			{
				event: ButtonActionTypes.ClickBackEditShippingAddressButton,
				actionMetaData: {
					orderNumber: props.order.orderNumber,
				},
			},
			getCaseId()
		)
		props.close()
	}

	return (
		<div>
			<Dialog
				open={props.open}
				onClose={props.close}
				TransitionComponent={Transition}
				keepMounted
				fullWidth={true}
			>
				<DialogTitle>Shipping Address</DialogTitle>
				<DialogContent>
					<Grid
						container
						justifyContent="space-between"
						spacing={2}
						style={{marginTop: "2px", marginBottom: "10px"}}
					>
						<Grid item xs={6}>
							<TextField
								id="firstName"
								label="First Name"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={firstName}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField
								id="lastName"
								label="Last Name"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={lastName}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						{props.order.orderData.shippingAddress &&
							props.order.orderData.shippingAddress.email && (
								<Grid item xs={12}>
									<TextField
										id="email"
										label="Email"
										variant="outlined"
										style={{width: "100%"}}
										value={
											props.order.orderData.shippingAddress &&
											props.order.orderData.shippingAddress.email
												? props.order.orderData.shippingAddress.email
												: " "
										}
										disabled={true}
										inputProps={{maxLength: 33}}
									/>
								</Grid>
							)}
						<Grid item xs={12}>
							<TextField
								id="company"
								label="Company"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={company}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								id="streetName"
								label="Street Name"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={streetName}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								id="additionalStreetInfo"
								label="Additional Street Info"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={additionalStreetInfo}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								id="city"
								label="City / Town"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={city}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={8}>
							<TextField
								id="state"
								label="State / Province"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={state}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={4}>
							<TextField
								id="postalCode"
								label="Postal Code"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={postalCode}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={9}>
							<TextField
								id="phone"
								label="Phone"
								variant="outlined"
								onChange={handleChange}
								style={{width: "100%"}}
								value={phone}
								disabled={false}
								inputProps={{maxLength: 33}}
							/>
						</Grid>
						<Grid item xs={3}>
							<TextField
								id="country"
								label="Country"
								variant="outlined"
								style={{width: "100%"}}
								value={
									props.order.orderData.shippingAddress &&
									props.order.orderData.shippingAddress.country
										? props.order.orderData.shippingAddress.country
										: " "
								}
								disabled={true}
								inputProps={{maxLength: 2}}
							/>
						</Grid>
					</Grid>
					<Divider style={{width: "100%"}} />
					<Dropdown
						label={"Reason"}
						helperText={"Why do you want to change the shipping address for this order?"}
						values={reasons}
						update={setReason}
					></Dropdown>
				</DialogContent>
				<DialogActions disableSpacing>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button onClick={onBackClick} variant="contained" color="secondary" size="small">
								back
							</Button>
						</Grid>
						<Grid item>
							<Button onClick={onSubmit} variant="contained" color="primary" size="small">
								submit
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			{popType !== null && (
				<AlertWithStatus
					open={popOpened}
					status={popType}
					message={popMessage}
					closeAlert={() => {
						setPopOpened(false)
					}}
				/>
			)}
		</div>
	)
}

const updateShippingAddress = async (data: ShippingAddressChangeRequest): Promise<boolean> => {
	const svc = new OrderService()
	const shippingAddressChanged = await svc.updateShippingAddress(data)
	return shippingAddressChanged
}
